﻿<script setup lang="ts">
    import { $Resources } from '@cwd/lib/main';
    import List from '@shared/components/List.vue';
    import { inject, onMounted, reactive } from 'vue';
    import GroupEdit from './GroupEdit.vue';
    import GroupListItem from './GroupListItem.vue';
    import { Group } from './Groups.access';
    import { fetchGroups, saveGroupInfo } from './services/groupService';
    import { Permissions } from '@shared/Root/Permissions';
    import { ScopeAccess, vRequireScopeParams } from '@shared/directives/Scope.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const state = reactive<{
        groups: Group[] | null;
        selectedGroup: Group | null;
        isLoading: boolean;
        enableNewGroup: boolean;
        newGroupName: string;
        hasScope: boolean;
    }>({
        groups: null,
        selectedGroup: null,
        isLoading: false,
        enableNewGroup: false,
        newGroupName: '',
        hasScope: false,
    });

    onMounted(async () => {
        const requireScopeParams: vRequireScopeParams = {
            Feature: 'Account',
            Permission: Permissions.Write,
        };
        state.hasScope = scopeAccess.hasScope(requireScopeParams);
        await getGroups();
    });

    async function getGroups() {
        if (!state.hasScope) return;
        state.isLoading = true;
        state.groups = await fetchGroups();
        state.isLoading = false;
    }

    const filterOptions = () => {
        return ['Name'];
    };

    const orderOptions = () => {
        return [{ display: $Resources.NameLabel, key: 'Name' }];
    };

    function backAndDelete(selectedGroupId: string): void {
        const group = state.groups?.find((g) => g.GroupId === selectedGroupId);
        if (group) state.groups = state.groups?.filter((g) => g.GroupId !== selectedGroupId) || [];
        state.selectedGroup = null;
    }

    function select(selectedGroupId: string): void {
        const group = state.groups?.find((g) => g.GroupId === selectedGroupId);
        if (!group) {
            state.selectedGroup = null;
        } else {
            state.selectedGroup = group;
        }
    }

    function handleAbortNewGroup() {
        state.newGroupName = '';
        state.enableNewGroup = false;
    }

    function handleNewGroup() {
        state.enableNewGroup = true;
    }

    async function handleCreateNewGroup() {
        state.enableNewGroup = false;
        const newId = await saveGroupInfo({ name: state.newGroupName });
        if (!newId) return;
        state.groups
            ? state.groups.push({ Name: state.newGroupName, GroupId: newId, UserCount: 0, ObjectCount: 0 })
            : (state.groups = [{ Name: state.newGroupName, GroupId: newId, UserCount: 0, ObjectCount: 0 }]);
    }
</script>

<template>
    <template v-if="state.selectedGroup == null">
        <List
            itemId="GroupId"
            :title="$Resources.AclGroupHeading"
            :perPage="10"
            :items="state.groups"
            :loading="state.isLoading"
            :filterKeys="filterOptions()"
            :orderOptions="orderOptions()">
            <template #item="{ item, index }">
                <GroupListItem :group="item" :index="index" @select="select"></GroupListItem>
            </template>
            <template #toolbar>
                <div v-if="!state.enableNewGroup">
                    <button class="btn btn-primary" @click="handleNewGroup" type="button">
                        {{ $Resources.AclNewGroupBtn }}
                    </button>
                </div>

                <div v-else>
                    <div class="d-inline-flex gap-sm">
                        <div class="flex-1">
                            <label class="sr-only" for="aclNewGroup">{{ $Resources.AclNewGroupLabel }}</label>
                            <input
                                id="aclNewGroup"
                                :placeholder="$Resources.AclNewGroupLabel"
                                class="form-control"
                                v-model="state.newGroupName"
                                style="width: 300px" />
                        </div>

                        <button class="btn btn-primary" type="button" @click="handleCreateNewGroup">
                            {{ $Resources.AclCreateTooltip }}
                        </button>

                        <button class="btn btn-subtle" type="button" @click="handleAbortNewGroup">
                            {{ $Resources.AclAbortTooltip }}
                        </button>
                    </div>
                </div>
            </template>
        </List>
    </template>
    <template v-else>
        <GroupEdit :group="state.selectedGroup" @back="backAndDelete"></GroupEdit>
    </template>
</template>

<style></style>
